import React from "react";

// A measured dimension, rendered as a position on a track with the healthy
// range marked — never as a score.
//
// This is the counterpart to the radar, and the split is deliberate. A rubric
// parameter is JUDGED 0-10 against a written anchor, and further out is better.
// A measure is MEASURED: 62% talk time is 62%, and it has a healthy BAND, not a
// direction. Talking 5% of the time is not ten-out-of-ten listening — on a demo
// it may mean the rep never actually presented anything.
//
// Two kinds of number, two visual languages, never mixed on one chart.
//
// Before calibration there is no verdict. Bands derived from a blog post and
// applied to people whose commission depends on them are worse than no bands,
// so a measure whose status is "uncalibrated" shows its value and its band as
// context and colours nothing red. Flipping that on is a decision someone makes
// on ~200 of our own scored calls, not a default that drifts into place.

const FORMATTERS = {
  ratio: (v) => `${Math.round(v * 100)}%`,
  seconds: (v) => `${Math.round(v)}s`,
  per_min: (v) => `${v.toFixed(1)} /min`,
  per_100w: (v) => `${v.toFixed(1)} /100w`,
  wpm: (v) => `${Math.round(v)} wpm`,
  pct_into_call: (v) => `${Math.round(v * 100)}% into the call`,
  plain: (v) => (typeof v === "number" ? String(Math.round(v * 10) / 10) : String(v)),
};

// What each measure is called in the rep's language, its unit, and the range
// it sits inside for display purposes. `band` is context, not judgement.
export const MEASURE_DISPLAY = {
  talk_to_listen:      { label: "You talked",        format: "ratio",   scale: [0, 1],   band: [0.45, 0.55] },
  interchanges_per_min:{ label: "Back-and-forth",    format: "per_min", scale: [0, 10],  band: [3, 6] },
  longest_monologue_s: { label: "Longest stretch",   format: "seconds", scale: [0, 240], band: [0, 90] },
  interruption_rate_10m:{ label: "Talking over them",format: "plain",   scale: [0, 10],  band: [0, 2] },
  speaking_pace_wpm:   { label: "Speaking pace",     format: "wpm",     scale: [80, 240],band: [130, 170] },
  filler_per_100w:     { label: "Filler words",      format: "per_100w",scale: [0, 20],  band: [0, 3] },
  open_question_ratio: { label: "Open questions",    format: "ratio",   scale: [0, 1],   band: [0.4, 1] },
  price_first_mention_pct: { label: "Price came up at", format: "pct_into_call", scale: [0, 1], band: [0.5, 1] },
};

export default function BandGauge({ id, value, priorValue = null, calibrated = false }) {
  const spec = MEASURE_DISPLAY[id];
  if (!spec || value == null || typeof value !== "number") return null;

  const [lo, hi] = spec.scale;
  const pct = (v) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
  const format = FORMATTERS[spec.format] || FORMATTERS.plain;

  const [bandLo, bandHi] = spec.band;
  const outOfBand = value < bandLo || value > bandHi;
  // The verdict is withheld until the bands come from our own calls. Until
  // then the marked range is shown as context and the value is never coloured.
  const colour = !calibrated ? "var(--c-700)"
    : outOfBand ? "var(--red-600, #c0392b)" : "var(--green-600)";

  return (
    <div style={{ padding: "6px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--c-700)" }}>{spec.label}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: colour, fontVariantNumeric: "tabular-nums" }}>
          {format(value)}
        </span>
      </div>

      <div style={{ position: "relative", height: 8, marginTop: 4, borderRadius: 999,
                    background: "var(--c-100, rgba(120,120,120,.15))" }}>
        {/* the healthy range, as a region rather than a threshold */}
        <div style={{
          position: "absolute", left: `${pct(bandLo)}%`, width: `${Math.max(2, pct(bandHi) - pct(bandLo))}%`,
          top: 0, bottom: 0, borderRadius: 999, background: "var(--green-600)", opacity: 0.18,
        }} />
        {/* where this rep sat last month, when we know it — the comparison that
            actually matters for pace is against their own baseline, not an
            absolute anybody published */}
        {priorValue != null && (
          <div title={`Previously ${format(priorValue)}`} style={{
            position: "absolute", left: `${pct(priorValue)}%`, top: -2, bottom: -2,
            width: 2, background: "var(--c-400)", borderRadius: 2,
          }} />
        )}
        <div style={{
          position: "absolute", left: `calc(${pct(value)}% - 5px)`, top: -1,
          width: 10, height: 10, borderRadius: 999, background: colour,
          border: "2px solid var(--surface)",
        }} />
      </div>

      <div style={{ fontSize: 10, color: "var(--c-500)", marginTop: 3 }}>
        healthy {format(bandLo)}–{format(bandHi)}
        {priorValue != null && ` · you before: ${format(priorValue)}`}
        {!calibrated && " · not yet calibrated on our own calls"}
      </div>
    </div>
  );
}

export function DynamicsStrip({ measures, calibrated = false, priorMeasures = {} }) {
  if (!measures || typeof measures !== "object") return null;
  const shown = Object.keys(MEASURE_DISPLAY).filter((id) => typeof measures[id] === "number");
  if (!shown.length) {
    // Withheld, not estimated. If the rep's speaker couldn't be attributed we
    // say so rather than attributing to the most talkative voice and printing a
    // precise, wrong ratio.
    const withheld = measures?._confidence?.withheld;
    return withheld ? (
      <p style={{ fontSize: 11.5, color: "var(--c-500)", margin: "6px 0 0" }}>
        Call dynamics aren't shown for this recording — we couldn't separate the speakers
        confidently enough to trust the numbers.
      </p>
    ) : null;
  }

  return (
    <div>
      {shown.map((id) => (
        <BandGauge key={id} id={id} value={measures[id]}
                   priorValue={priorMeasures?.[id] ?? null} calibrated={calibrated} />
      ))}
    </div>
  );
}
